import {
  NodeUtils,
  createBlock,
  mergeBlock,
  t,
} from "@easy-email-pro-core/utils";

import React from "react";

// only use in testing mode
import testingCss from "./testing.scss?inline";

// only use in production mode
import basicCss from "./basic.css?inline";

import inlineBasicCss from "./inline-basic.css?inline";
import { BasicType, ElementCategory } from "@easy-email-pro-core/constants";
import {
  ElementDefinition,
  Element,
  PageElement,
} from "@easy-email-pro-core/typings";
import { BlockRenderer } from "@easy-email-pro-core/components";

const defaultData: ElementDefinition<PageElement>["defaultData"] = {
  attributes: {
    width: "600px",
    "margin-top": "30px",
    "margin-bottom": "30px",
    "link-text-decoration": "underline",
  },
  data: { breakpoint: "480px" },
};

export const Page = createBlock<PageElement>({
  get name() {
    return t("Page");
  },
  defaultData,
  type: BasicType.PAGE,
  create: (payload) => {
    const defaultData: PageElement = {
      type: BasicType.PAGE,
      data: {
        breakpoint: "480px",
        globalAttributes: {
          "font-family": "Arial, sans-serif",
        },
      },
      attributes: {},
      children: [],
    };
    return mergeBlock(defaultData, payload);
  },
  category: ElementCategory.PAGE,
  render(params) {
    const {
      node,
      node: { data, attributes },
      mode,
    } = params;

    const breakpoint = data.breakpoint
      ? `<mj-breakpoint width="${data.breakpoint}" />`
      : "";

    const styles = [...(data.headStyles || [])];

    if (mode === "testing") {
      styles.unshift({ content: testingCss });
    } else {
      styles.unshift({
        content: basicCss,
      });
      styles.unshift({
        content: inlineBasicCss,
        inline: "inline",
      });
    }

    const stylesText =
      styles
        .map(
          (style) =>
            `<mj-style ${style.inline ? 'inline="inline"' : ""}>${
              style.content
            }</mj-style>`
        )
        .join("\n") || "";

    // 处理链接
    const linkAttributes: {
      color?: string;
      "font-weight"?: string;
      "text-decoration"?: string;
      "font-style"?: string;
    } = {
      color: attributes["link-color"]?.trim() || "inherit",
      "font-weight": attributes["link-font-weight"]?.trim(),
      "text-decoration": attributes["link-text-decoration"]?.trim(),
      "font-style": attributes["link-font-style"]?.trim(),
    };

    const linkAttributesText = Object.keys(linkAttributes)
      .map((item) => {
        const map = linkAttributes as any;
        if (map[item]) return `${item}: ${map[item]}`;
        return "";
      })
      .filter(Boolean)
      .join(";");
    const linkStyle = `<mj-style>.mjbody a, .mjbody a:hover, .mjbody a:active, .mjbody a[href], .mjbody a[href]:hover, .mjbody a[href]:active {${linkAttributesText}}</mj-style>`;

    const backgroundColor = attributes["background-color"]
      ? `background-color="${attributes["background-color"]}"`
      : "";

    const marginTopSpacer =
      attributes["margin-top"] && attributes["margin-top"] !== "0px"
        ? `<mj-section padding="0px"><mj-column><mj-spacer height="${attributes["margin-top"]}"></mj-spacer></mj-column></mj-section>`
        : "";

    const marginBottomSpacer =
      attributes["margin-bottom"] && attributes["margin-bottom"] !== "0px"
        ? `<mj-section padding="0px"><mj-column><mj-spacer height="${attributes["margin-top"]}"></mj-spacer></mj-column></mj-section>`
        : "";

    const preheader = data["preheader"]
      ? `<mj-preview>${data["preheader"]}</mj-preview>`
      : "";

    const pageWidth = attributes.width || "600px";

    return (
      <>
        {`
          <mjml>
          <mj-head>
              ${stylesText}
              ${linkStyle}
              <!-- GLOBAL_EXTRACT_HEADER -->
              ${breakpoint}
              ${preheader}
              ${
                data.fonts?.length
                  ? data.fonts
                      ?.filter(Boolean)
                      .map(
                        (item) =>
                          `<mj-font name="${item.name}" href="${item.href}" />`
                      )
                  : ""
              }

          </mj-head>
          <mj-body width="${pageWidth}" ${backgroundColor} css-class="mjbody">
          ${marginTopSpacer}
          `}
        {`<!-- EASY_EMAIL_FROZEN_HEADER -->`}
        {node.children.map((child, index) => {
          return (
            <BlockRenderer
              {...params}
              idx={NodeUtils.getChildIdx(NodeUtils.getPageIdx(), index)}
              key={index}
              node={child as Element}
            />
          );
        })}

        {`<!-- EASY_EMAIL_FROZEN_FOOTER -->`}
        {`<!-- EASY_EMAIL_BRAND -->`}

        {`
        ${marginBottomSpacer}

        </mj-body></mjml>`}
      </>
    );
  },
});

export const PageTestingCss = testingCss;
